import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { aiService } from "@/lib/ai-service";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/primitives";
import {
	MessageCircle,
	Send,
	Loader2,
	Calendar,
	Brain,
	ChevronDown,
	ChevronUp,
	Copy,
	Check,
} from "lucide-react";
import { ChatMessage } from "./ChatMessage";

interface Message {
	id: string;
	type: "user" | "assistant";
	content: string;
	timestamp: Date;
}

interface DailyCheckIn {
	id: string;
	general_feeling: number;
	day_description: string;
	created_at: string;
}

type TimeRange = "30days" | "3months";

/**
 * Formats AI response data from JSON into human-readable text
 */
const formatAIResponseData = (data: any): string => {
	if (!data || typeof data !== "object") {
		return "I couldn't analyze your check-ins right now.";
	}

	let formattedText = "";

	// Handle main positive themes
	if (data.main_positive_themes_last_7_days) {
		formattedText += "**Main positive themes from your recent check-ins:**\n\n";
		data.main_positive_themes_last_7_days.forEach(
			(theme: string, index: number) => {
				formattedText += `${index + 1}. ${theme}\n`;
			},
		);
		formattedText += "\n";
	}

	// Handle main challenges
	if (data.main_challenges_last_7_days) {
		formattedText += "**Main challenges from your recent check-ins:**\n\n";
		data.main_challenges_last_7_days.forEach(
			(challenge: string, index: number) => {
				formattedText += `${index + 1}. ${challenge}\n`;
			},
		);
		formattedText += "\n";
	}

	// Handle overall mood trends
	if (data.mood_trend) {
		formattedText += `**Overall mood trend:** ${data.mood_trend}\n\n`;
	}

	// Handle insights
	if (data.insights) {
		formattedText += "**Key insights:**\n\n";
		if (Array.isArray(data.insights)) {
			data.insights.forEach((insight: string, index: number) => {
				formattedText += `${index + 1}. ${insight}\n`;
			});
		} else {
			formattedText += `${data.insights}\n`;
		}
		formattedText += "\n";
	}

	// Handle recommendations
	if (data.recommendations) {
		formattedText += "**Recommendations:**\n\n";
		if (Array.isArray(data.recommendations)) {
			data.recommendations.forEach((rec: string, index: number) => {
				formattedText += `${index + 1}. ${rec}\n`;
			});
		} else {
			formattedText += `${data.recommendations}\n`;
		}
		formattedText += "\n";
	}

	// Handle patterns
	if (data.patterns) {
		formattedText += "**Patterns I noticed:**\n\n";
		if (Array.isArray(data.patterns)) {
			data.patterns.forEach((pattern: string, index: number) => {
				formattedText += `• ${pattern}\n`;
			});
		} else {
			formattedText += `${data.patterns}\n`;
		}
		formattedText += "\n";
	}

	// Handle summary
	if (data.summary) {
		formattedText += `**Summary:** ${data.summary}\n\n`;
	}

	// If we still have empty text, try to extract any text values
	if (!formattedText.trim()) {
		const textValues = [];

		for (const [key, value] of Object.entries(data)) {
			if (typeof value === "string" && value.length > 0) {
				const formattedKey = key
					.replace(/_/g, " ")
					.replace(/\b\w/g, (l) => l.toUpperCase());
				textValues.push(`**${formattedKey}:** ${value}`);
			} else if (Array.isArray(value) && value.length > 0) {
				const formattedKey = key
					.replace(/_/g, " ")
					.replace(/\b\w/g, (l) => l.toUpperCase());
				textValues.push(`**${formattedKey}:**`);
				value.forEach((item: any, index: number) => {
					if (typeof item === "string") {
						textValues.push(`${index + 1}. ${item}`);
					}
				});
			}
		}

		if (textValues.length > 0) {
			formattedText = textValues.join("\n\n") + "\n";
		}
	}

	// Final fallback
	if (!formattedText.trim()) {
		formattedText =
			"I've analyzed your check-ins, but I'm having trouble formatting the response. Please try asking your question in a different way.";
	}

	return formattedText.trim();
};

export function CheckInChatInterface() {
	const { user, profile } = useAuth();
	const [messages, setMessages] = useState<Message[]>([]);
	const [inputValue, setInputValue] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [timeRange, setTimeRange] = useState<TimeRange>("3months");
	const [checkIns, setCheckIns] = useState<DailyCheckIn[]>([]);
	const [isMinimized, setIsMinimized] = useState(true);
	const [fetchingCheckIns, setFetchingCheckIns] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const messagesContainerRef = useRef<HTMLDivElement>(null);

	// Load messages from database
	useEffect(() => {
		if (user) {
			loadMessagesFromDatabase();
		}
	}, [user, timeRange]);

	const loadMessagesFromDatabase = async () => {
		if (!user) return;

		try {
			const { data, error } = await supabase
				.from("checkin_chat_messages")
				.select("id, message_type, content, created_at")
				.eq("user_id", user.id)
				.eq("time_range", timeRange)
				.order("created_at", { ascending: true })
				.limit(20); // Only get last 20 messages

			if (error) {
				console.error("Error loading messages from database:", error);
				// Fallback to localStorage if database fails
				loadMessagesFromLocalStorage();
				return;
			}

			if (data && data.length > 0) {
				const messagesWithDates = data.map((msg) => ({
					id: msg.id,
					type: msg.message_type as "user" | "assistant",
					content: msg.content,
					timestamp: new Date(msg.created_at),
				}));
				setMessages(messagesWithDates);
				// Auto-expand if there are messages
				setIsMinimized(false);
			} else {
				// No messages in database, try localStorage as fallback
				loadMessagesFromLocalStorage();
			}
		} catch (error) {
			console.error("Error loading messages:", error);
			// Fallback to localStorage
			loadMessagesFromLocalStorage();
		}
	};

	const loadMessagesFromLocalStorage = () => {
		if (!user) return;
		try {
			const storageKey = `checkin_chat_messages_${user.id}_${timeRange}`;
			const savedMessages = localStorage.getItem(storageKey);
			if (savedMessages) {
				const parsed = JSON.parse(savedMessages);
				const messagesWithDates = parsed.map((msg: any) => ({
					...msg,
					timestamp: new Date(msg.timestamp),
				}));
				setMessages(messagesWithDates);
				if (messagesWithDates.length > 0) {
					setIsMinimized(false);
				}
			} else {
				setMessages([]);
			}
		} catch (error) {
			console.error("Error loading from localStorage:", error);
			setMessages([]);
		}
	};

	const saveMessageToDatabase = async (
		message: Message,
	): Promise<string | null> => {
		if (!user) return null;

		try {
			const { data, error } = await supabase
				.from("checkin_chat_messages")
				.insert({
					user_id: user.id,
					time_range: timeRange,
					message_type: message.type,
					content: message.content,
				})
				.select("id")
				.single();

			if (error) {
				console.error("Error saving message to database:", error);
				// Fallback to localStorage
				saveMessageToLocalStorage(message);
				return null;
			}

			// After saving, check if we have more than 20 messages and delete old ones
			await cleanupOldMessages();

			return data?.id || null;
		} catch (error) {
			console.error("Error saving message:", error);
			// Fallback to localStorage
			saveMessageToLocalStorage(message);
			return null;
		}
	};

	const cleanupOldMessages = async () => {
		if (!user) return;

		try {
			// Get all messages for this user and time range, ordered by created_at
			const { data, error: fetchError } = await supabase
				.from("checkin_chat_messages")
				.select("id, created_at")
				.eq("user_id", user.id)
				.eq("time_range", timeRange)
				.order("created_at", { ascending: false });

			if (fetchError) {
				console.error("Error fetching messages for cleanup:", fetchError);
				return;
			}

			// If we have more than 20 messages, delete the oldest ones
			if (data && data.length > 20) {
				const messagesToDelete = data.slice(20); // Get all messages after the first 20
				const idsToDelete = messagesToDelete.map((msg) => msg.id);

				const { error: deleteError } = await supabase
					.from("checkin_chat_messages")
					.delete()
					.in("id", idsToDelete);

				if (deleteError) {
					console.error("Error deleting old messages:", deleteError);
				}
			}
		} catch (error) {
			console.error("Error in cleanup:", error);
		}
	};

	const saveMessageToLocalStorage = (message: Message) => {
		if (!user) return;
		try {
			const storageKey = `checkin_chat_messages_${user.id}_${timeRange}`;
			const existingMessages = localStorage.getItem(storageKey);
			let messages: Message[] = existingMessages
				? JSON.parse(existingMessages)
				: [];

			// Add new message
			messages.push(message);

			// Keep only last 20 messages
			if (messages.length > 20) {
				messages = messages.slice(-20);
			}

			localStorage.setItem(storageKey, JSON.stringify(messages));
		} catch (error) {
			console.error("Error saving to localStorage:", error);
		}
	};

	// Auto-expand when messages are added
	useEffect(() => {
		if (messages.length > 0 && isMinimized) {
			setIsMinimized(false);
		}
	}, [messages.length]);

	useEffect(() => {
		if (user) {
			fetchCheckIns();
		}
	}, [user, timeRange]);

	// Auto-scroll to bottom when messages are added (like WhatsApp)
	// This handles both initial load and new messages
	useEffect(() => {
		// Scroll to bottom when chat is expanded and messages exist
		if (!isMinimized && messages.length > 0) {
			// Use requestAnimationFrame + setTimeout to ensure DOM is fully updated
			let rafId: number;
			let timer: NodeJS.Timeout;

			rafId = requestAnimationFrame(() => {
				timer = setTimeout(() => {
					if (messagesContainerRef.current) {
						// Ensure we scroll to the very bottom
						messagesContainerRef.current.scrollTop =
							messagesContainerRef.current.scrollHeight;
					}
				}, 100);
			});

			return () => {
				cancelAnimationFrame(rafId);
				if (timer) clearTimeout(timer);
			};
		}
	}, [messages.length, isMinimized]);

	const fetchCheckIns = async () => {
		if (!user) return;

		setFetchingCheckIns(true);
		setError(null);

		try {
			const daysToFetch = timeRange === "30days" ? 30 : 90;
			const cutoffDate = new Date();
			cutoffDate.setDate(cutoffDate.getDate() - daysToFetch);

			const { data, error } = await supabase
				.from("daily_checkins")
				.select("id, general_feeling, day_description, created_at")
				.eq("user_id", user.id)
				.gte("created_at", cutoffDate.toISOString())
				.order("created_at", { ascending: true });

			if (error) {
				console.error("Error fetching check-ins:", error);
				setError("Failed to load check-ins. Please try again.");
				return;
			}

			setCheckIns(data || []);
		} catch (error) {
			console.error("Error fetching check-ins:", error);
			setError("Failed to load check-ins. Please try again.");
		} finally {
			setFetchingCheckIns(false);
		}
	};

	const generateResponse = async (userQuestion: string): Promise<string> => {
		try {
			// If no check-ins, provide guidance
			if (checkIns.length === 0) {
				return "I don't see any check-ins in your selected time range. Try adding some daily check-ins first, then I can help you analyze patterns and insights from your data.";
			}

			// Prepare context from check-ins with better formatting
			const checkInContext = checkIns
				.map((checkIn, index) => {
					const date = new Date(checkIn.created_at).toLocaleDateString();
					const feelingText =
						checkIn.general_feeling >= 4
							? "positive"
							: checkIn.general_feeling <= 2
								? "negative"
								: "neutral";
					return `${index + 1}. ${date}: Feeling ${feelingText} (${checkIn.general_feeling}), Notes: "${checkIn.day_description}"`;
				})
				.join("\n");

			// Create a specialized prompt for chat-style responses
			const chatPrompt = `You are a supportive AI assistant helping someone understand their daily check-in patterns, reflections, stress levels, and performance. Be conversational, empathetic, and insightful.

IMPORTANT: Only answer questions related to:
- Daily check-ins and mood patterns
- Reflections and insights
- Stress levels and management
- Performance, productivity, and goal achievement
- Wellness, health, and work-life balance patterns

If the question is not related to these topics, politely redirect the user to ask about their data instead.

CONTEXT: Here are their check-ins from the ${timeRange === "30days" ? "last 30 days" : "last 3 months"} (${checkIns.length} total):

${checkInContext}

USER QUESTION: "${userQuestion}"

INSTRUCTIONS:
- Only respond if the question is about check-ins, reflections, stress, or performance
- Provide a helpful, conversational response
- Analyze patterns if relevant to the question
- Be supportive and professional
- Keep responses concise but meaningful
- If you notice concerning patterns, gently suggest professional support
- Focus on actionable insights when possible

Response:`;

			// Use a direct fetch to the Supabase function for better control
			let data, error;
			try {
				const result = await supabase.functions.invoke("ai-generate", {
					body: {
						promptType: "overall_context",
						userInput: chatPrompt,
					},
				});
				data = result.data;
				error = result.error;
			} catch (invokeError: any) {
				const status = (invokeError && (invokeError as any).status) || 500;
				console.error(
					"Edge Function invoke failed:",
					(invokeError && (invokeError as any).message) || String(invokeError),
				);
				if (status === 404) {
					console.warn(
						`💡 Edge Functions not deployed. Run: supabase functions deploy --project-ref ${import.meta.env.VITE_SUPABASE_PROJECT_ID}`,
					);
				}
				error = {
					message: (invokeError as any)?.message || "Edge Function call failed",
					status: status,
				};
				data = null;
			}

			if (error) {
				console.error("AI service error:", error);
				throw error;
			}

			// Handle different response formats more robustly
			console.log("🔍 CheckIn Chat AI Response:", data);

			if (typeof data === "string") {
				return data;
			} else if (data && typeof data === "object") {
				const responseText =
					data.response ||
					data.answer ||
					data.content ||
					data.text ||
					data.message ||
					formatAIResponseData(data);

				if (responseText && typeof responseText === "string") {
					return responseText;
				}
			}

			console.error("🚨 AI service returned unexpected format:", {
				data,
				type: typeof data,
			});
			throw new Error(
				"AI service returned invalid response format. Please check AI service configuration.",
			);
		} catch (error) {
			console.error("Error generating AI response:", error);
			throw error;
		}
	};

	const isQuestionRelevant = (question: string): boolean => {
		const lowerQuestion = question.toLowerCase();

		// Keywords related to check-ins, reflections, stress, and performance
		const relevantKeywords = [
			// Check-in related
			"check-in",
			"checkin",
			"daily",
			"day",
			"mood",
			"feeling",
			"feelings",
			"emotion",
			"emotions",
			"wellbeing",
			"well-being",
			"wellness",

			// Reflection related
			"reflection",
			"reflections",
			"reflect",
			"insight",
			"insights",
			"pattern",
			"patterns",
			"trend",
			"trends",
			"progress",

			// Stress related
			"stress",
			"stressed",
			"anxiety",
			"anxious",
			"worried",
			"worry",
			"pressure",
			"overwhelmed",
			"tension",
			"strain",

			// Performance related
			"performance",
			"perform",
			"productivity",
			"productive",
			"efficiency",
			"efficient",
			"focus",
			"concentration",
			"energy",
			"energetic",
			"motivation",
			"motivated",
			"achievement",
			"achieve",
			"goal",
			"goals",

			// General wellness terms
			"sleep",
			"exercise",
			"health",
			"mental",
			"physical",
			"work",
			"life",
			"balance",
			"coping",
			"manage",
			"improve",
			"better",
			"worse",
			"difficult",
			"challenge",
			"challenges",
			"struggle",
			"positive",
			"negative",
			"improvement",
			"decline",
		];

		// Check if question contains any relevant keywords
		const hasRelevantKeyword = relevantKeywords.some((keyword) =>
			lowerQuestion.includes(keyword),
		);

		// Also check for question patterns that might be about the user's data
		const questionPatterns = [
			/\b(how|what|when|where|why|which)\b.*\b(my|me|i|myself)\b/i,
			/\b(show|tell|analyze|explain|describe)\b.*\b(my|me)\b/i,
			/\b(compare|difference|similar)\b/i,
			/\b(overall|summary|summary|insight)\b/i,
		];

		const hasRelevantPattern = questionPatterns.some((pattern) =>
			pattern.test(question),
		);

		return hasRelevantKeyword || hasRelevantPattern;
	};

	const handleSendMessage = async (
		e?: React.MouseEvent | React.KeyboardEvent,
	) => {
		// Prevent default behavior to avoid scrolling
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}

		if (!inputValue.trim() || isLoading) return;

		const trimmedInput = inputValue.trim();

		// Validate that the question is relevant to check-ins, reflections, stress, or performance
		if (!isQuestionRelevant(trimmedInput)) {
			const errorMessage: Message = {
				id: `temp-${Date.now()}`,
				type: "assistant",
				content:
					'I\'m here to help you understand your check-ins, reflections, stress levels, and performance patterns. Could you please ask a question about your data? For example:\n\n• "What patterns do you see in my check-ins?"\n• "How has my stress level changed?"\n• "What insights can you share about my reflections?"\n• "How is my performance trending?"',
				timestamp: new Date(),
			};

			setMessages((prev) => {
				const newMessages = [...prev, errorMessage];
				return newMessages.slice(-20);
			});

			setInputValue("");
			return;
		}

		const userMessage: Message = {
			id: `temp-${Date.now()}`, // Temporary ID, will be replaced with DB ID
			type: "user",
			content: trimmedInput,
			timestamp: new Date(),
		};

		setMessages((prev) => {
			const newMessages = [...prev, userMessage];
			// Keep only last 20 messages in state
			return newMessages.slice(-20);
		});
		setInputValue("");
		setIsLoading(true);

		// Save user message to database and update ID if successful
		const userMessageId = await saveMessageToDatabase(userMessage);
		if (userMessageId) {
			setMessages((prev) => {
				const updated = prev.map((msg) =>
					msg.id === userMessage.id ? { ...msg, id: userMessageId } : msg,
				);
				return updated;
			});
		}

		try {
			const response = await generateResponse(userMessage.content);

			const assistantMessage: Message = {
				id: `temp-${Date.now() + 1}`, // Temporary ID, will be replaced with DB ID
				type: "assistant",
				content: response,
				timestamp: new Date(),
			};

			setMessages((prev) => {
				const newMessages = [...prev, assistantMessage];
				// Keep only last 20 messages in state
				return newMessages.slice(-20);
			});

			// Save assistant message to database and update ID if successful
			const assistantMessageId = await saveMessageToDatabase(assistantMessage);
			if (assistantMessageId) {
				setMessages((prev) => {
					const updated = prev.map((msg) =>
						msg.id === assistantMessage.id
							? { ...msg, id: assistantMessageId }
							: msg,
					);
					return updated;
				});
			}
		} catch (error) {
			console.error("Error handling message:", error);
			const errorMessage: Message = {
				id: `temp-${Date.now() + 1}`, // Temporary ID, will be replaced with DB ID
				type: "assistant",
				content:
					"I apologize, but I'm having trouble responding right now. Please try again later.",
				timestamp: new Date(),
			};

			setMessages((prev) => {
				const newMessages = [...prev, errorMessage];
				// Keep only last 20 messages in state
				return newMessages.slice(-20);
			});

			// Save error message to database and update ID if successful
			const errorMessageId = await saveMessageToDatabase(errorMessage);
			if (errorMessageId) {
				setMessages((prev) => {
					const updated = prev.map((msg) =>
						msg.id === errorMessage.id ? { ...msg, id: errorMessageId } : msg,
					);
					return updated;
				});
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			e.stopPropagation();
			handleSendMessage(e);
		}
		// Shift+Enter allows new line (default behavior, no need to handle)
	};

	const copyMessages = async () => {
		if (messages.length === 0) return;

		try {
			// Format messages for clipboard
			const formattedMessages = messages
				.map((msg) => {
					const timestamp = msg.timestamp.toLocaleString();
					const sender =
						msg.type === "user" ? "You" : "Reflection Journal AI mentor";
					return `[${timestamp}] ${sender}:\n${msg.content}`;
				})
				.join("\n\n");

			// Copy to clipboard
			await navigator.clipboard.writeText(formattedMessages);

			// Show feedback
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (error) {
			console.error("Error copying messages:", error);
			// Fallback for older browsers
			try {
				const textArea = document.createElement("textarea");
				const formattedMessages = messages
					.map((msg) => {
						const timestamp = msg.timestamp.toLocaleString();
						const sender =
							msg.type === "user" ? "You" : "Reflection Journal AI mentor";
						return `[${timestamp}] ${sender}:\n${msg.content}`;
					})
					.join("\n\n");
				textArea.value = formattedMessages;
				textArea.style.position = "fixed";
				textArea.style.opacity = "0";
				document.body.appendChild(textArea);
				textArea.select();
				document.execCommand("copy");
				document.body.removeChild(textArea);
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			} catch (fallbackError) {
				console.error("Fallback copy failed:", fallbackError);
			}
		}
	};

	const clearChat = async () => {
		setMessages([]);

		// Clear from database
		if (user) {
			try {
				const { error } = await supabase
					.from("checkin_chat_messages")
					.delete()
					.eq("user_id", user.id)
					.eq("time_range", timeRange);

				if (error) {
					console.error("Error clearing messages from database:", error);
				}
			} catch (error) {
				console.error("Error clearing messages:", error);
			}

			// Also clear from localStorage
			try {
				const storageKey = `checkin_chat_messages_${user.id}_${timeRange}`;
				localStorage.removeItem(storageKey);
			} catch (error) {
				console.error("Error clearing persisted messages:", error);
			}
		}
	};

	if (checkIns.length === 0) {
		return (
			<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
				<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
					<div className="flex items-center justify-between gap-3">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
								<MessageCircle className="h-5 w-5 text-white" />
							</div>
							<div>
								<CardTitle className="text-xl font-semibold text-foreground">
									Ask About Your Check-ins
								</CardTitle>
								<CardDescription className="text-sm mt-1">
									Chat with AI about your daily check-in patterns
								</CardDescription>
							</div>
						</div>
						<div className="flex items-center gap-2 flex-shrink-0">
							<Select
								value={timeRange}
								onValueChange={async (value: TimeRange) => {
									setTimeRange(value);
									// Clear messages when time range changes since context changes
									setMessages([]);
									if (user) {
										// Clear from database
										try {
											const { error } = await supabase
												.from("checkin_chat_messages")
												.delete()
												.eq("user_id", user.id)
												.eq("time_range", timeRange);

											if (error) {
												console.error(
													"Error clearing messages from database:",
													error,
												);
											}
										} catch (error) {
											console.error("Error clearing messages:", error);
										}

										// Also clear from localStorage
										try {
											const oldStorageKey = `checkin_chat_messages_${user.id}_${timeRange}`;
											localStorage.removeItem(oldStorageKey);
										} catch (error) {
											console.error("Error clearing old messages:", error);
										}
									}
								}}
							>
								<SelectTrigger className="w-32 h-7 text-xs">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="30days">Last 30 days</SelectItem>
									<SelectItem value="3months">Last 3 months</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-6">
					<div className="flex flex-col items-center justify-center h-48 text-center">
						<div className="p-3 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
							<Calendar className="h-8 w-8 text-white" />
						</div>
						<h4 className="text-base font-medium text-foreground mb-2">
							No Check-ins Available
						</h4>
						<p className="text-sm text-muted-foreground">
							Add some daily check-ins to start chatting about your patterns and
							insights.
						</p>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card
			className={`border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm transition-all duration-300 flex flex-col ${isMinimized ? "h-auto" : "h-[500px]"}`}
		>
			<CardHeader
				className="cursor-pointer p-4 pb-3 flex flex-row items-center justify-between gap-2 space-y-0 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 flex-shrink-0"
				onClick={() => setIsMinimized(!isMinimized)}
			>
				<div className="flex items-center min-w-0 flex-1 gap-3">
					<div className="p-1.5 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex-shrink-0">
						<Brain className="h-4 w-4 text-white" />
					</div>
					<CardTitle className="text-lg font-semibold pl-0 truncate">
						Ask About Your Check-ins
					</CardTitle>
				</div>

				<div className="ml-auto flex items-center gap-2 flex-nowrap flex-shrink-0">
					<Select
						value={timeRange}
						onValueChange={async (value: TimeRange) => {
							setTimeRange(value);
							// Clear messages when time range changes since context changes
							setMessages([]);
							if (user) {
								// Clear from database
								try {
									const { error } = await supabase
										.from("checkin_chat_messages")
										.delete()
										.eq("user_id", user.id)
										.eq("time_range", timeRange);

									if (error) {
										console.error(
											"Error clearing messages from database:",
											error,
										);
									}
								} catch (error) {
									console.error("Error clearing messages:", error);
								}

								// Also clear from localStorage
								try {
									const oldStorageKey = `checkin_chat_messages_${user.id}_${timeRange}`;
									localStorage.removeItem(oldStorageKey);
								} catch (error) {
									console.error("Error clearing old messages:", error);
								}
							}
						}}
					>
						<SelectTrigger
							className="w-32 h-7 text-xs"
							onClick={(e) => e.stopPropagation()}
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="30days">Last 30 days</SelectItem>
							<SelectItem value="3months">Last 3 months</SelectItem>
						</SelectContent>
					</Select>
					{isMinimized ? (
						<ChevronUp
							className="h-4 w-4"
							onClick={(e) => {
								e.stopPropagation();
								setIsMinimized(false);
							}}
						/>
					) : (
						<ChevronDown
							className="h-4 w-4"
							onClick={(e) => {
								e.stopPropagation();
								setIsMinimized(true);
							}}
						/>
					)}
				</div>
			</CardHeader>

			{/* Messages Area - Only show when expanded */}
			{!isMinimized && (
				<CardContent className="flex flex-col flex-1 min-h-0 p-4 overflow-hidden">
					<div
						ref={messagesContainerRef}
						className="flex-1 overflow-y-auto space-y-3 mb-4 pr-2"
					>
						{messages.length === 0 && (
							<div className="text-center py-8">
								<Brain className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
								<p className="text-sm text-muted-foreground mb-2">
									Ask me anything about your check-ins!
								</p>
								<div className="text-xs text-muted-foreground space-y-1">
									<p>Try asking:</p>
									<p>• "What patterns do you see in my mood?"</p>
									<p>• "How have I been feeling lately?"</p>
									<p>• "What's affecting my emotional state?"</p>
									<p>• "Any suggestions for improvement?"</p>
								</div>
							</div>
						)}

						{messages.map((message) => (
							<ChatMessage key={message.id} message={message} />
						))}

						{isLoading && (
							<ChatMessage
								message={{
									id: "loading",
									type: "assistant",
									content: "Analyzing your check-ins...",
									timestamp: new Date(),
								}}
								isLoading
							/>
						)}
					</div>
				</CardContent>
			)}

			{/* Input Area - Always visible, even when minimized */}
			<CardContent
				className={`p-4 flex-shrink-0 ${isMinimized ? "pt-2" : "pt-0"}`}
			>
				<div className="flex space-x-2">
					<Input
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						onKeyPress={handleKeyPress}
						placeholder="Ask about your check-ins..."
						disabled={isLoading}
						className="flex-1 text-sm"
					/>
					<Button
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							handleSendMessage(e);
						}}
						disabled={!inputValue.trim() || isLoading}
						size="sm"
						className="shrink-0"
						type="button"
					>
						{isLoading ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Send className="h-4 w-4" />
						)}
					</Button>
					{messages.length > 0 && (
						<>
							<Button
								onClick={copyMessages}
								variant="outline"
								size="sm"
								className="shrink-0 text-xs"
								title="Copy all messages to clipboard"
							>
								{copied ? (
									<>
										<Check className="h-3 w-3 mr-1" />
										Copied
									</>
								) : (
									<>
										<Copy className="h-3 w-3 mr-1" />
										Copy
									</>
								)}
							</Button>
							<Button
								onClick={clearChat}
								variant="outline"
								size="sm"
								className="shrink-0 text-xs"
							>
								Clear
							</Button>
						</>
					)}
				</div>
			</CardContent>
		</Card>
	);
}
